/**
 * @fileoverview JobRateBadge — says which rates a job is charged on, at the
 * top of the job's Timesheets tab (MOR-1038 "make it obvious").
 *
 * Always visible, Standard included, so nobody has to wonder. A job on a
 * multiplier card or with frozen rates is highlighted. Org admins and owners
 * get a Change button; everyone else just reads it.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { Snowflake, Tag } from "lucide-react";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";

import { cn } from "@/utils/misc";
import { Button } from "@/ui/primitives";
import { JobRateSettingsDialog } from "./JobRateSettingsDialog";
import { describeMultiplier, formatIsoDate } from "./rateCards.helpers";

export function JobRateBadge({
  orgId,
  jobId,
}: {
  orgId: Id<"organizations">;
  jobId: Id<"jobs">;
}) {
  const { data: terms } = useQuery(
    convexQuery(api.timesheets.jobRates.getForJob, { jobId }),
  );
  const [open, setOpen] = useState(false);

  if (!terms) return null;
  const special = terms.rateCardName !== undefined || !!terms.ratesAsAt;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-3 py-2 text-sm",
        special
          ? "border-amber-300/70 bg-amber-50 text-amber-950 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-100"
          : "border-border/60 bg-background text-muted-foreground",
      )}
    >
      <span className="flex items-center gap-1.5">
        <Tag className="size-3.5" />
        <span className="font-medium">Rates:</span>
        {terms.rateCardName !== undefined && terms.multiplier !== undefined
          ? `${terms.rateCardName} (${describeMultiplier(terms.multiplier)})`
          : "Standard"}
        {terms.cardArchived && " · archived card"}
      </span>
      {terms.ratesAsAt && (
        <span className="flex items-center gap-1.5">
          <Snowflake className="size-3.5" />
          Frozen as at {formatIsoDate(terms.ratesAsAt)}
        </span>
      )}
      {terms.canEdit && (
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto h-7 text-xs"
          onClick={() => setOpen(true)}
        >
          Change
        </Button>
      )}
      {terms.canEdit && (
        <JobRateSettingsDialog
          open={open}
          orgId={orgId}
          jobId={jobId}
          current={terms}
          onOpenChange={setOpen}
        />
      )}
    </div>
  );
}
